/**
 * Sprite atlas loading with a procedural fallback for every key.
 *
 * `./atlas.ts` is generated by the art pipeline and may simply not exist. A
 * literal `import('./atlas.ts')` would be a hard build error in that case, so
 * we go through `import.meta.glob`, which resolves to `{}` when the file is
 * absent and to a properly bundled lazy loader when it is present. Nothing in
 * this module ever throws because of a missing atlas, a missing sprite key or a
 * sheet that failed to download.
 */
import type { AtlasManifest, SpriteDef } from './atlas-types.ts';
import { REQUIRED_SPRITES } from './atlas-types.ts';
import { consumeFallbackOps, drawFallbackSprite } from './fallback.ts';

export interface SpriteDrawOptions {
  /** Explicit animation frame. Omit to derive it from `timeS` and the sprite's fps. */
  frame?: number;
  /** Monotonic seconds, used for frame timing and animated fallbacks. */
  timeS?: number;
  alpha?: number;
  /**
   * The box the sprite should occupy. Atlas frames draw at their natural size
   * anchored to the box origin (ART sizes art to fit); the procedural fallback
   * fills the box. Set `stretch` to scale an atlas frame to the box instead.
   */
  w?: number;
  h?: number;
  stretch?: boolean;
  /** Free parameter handed to the fallback painter. */
  extra?: number;
}

type SheetState = 'pending' | 'ready' | 'failed';

const DEFAULT_FPS = 8;
export const DEFAULT_SHEET_TIMEOUT_MS = 6000;

export interface SpriteSystemOptions {
  /**
   * How long to wait for a sheet image before giving up and using the
   * procedural fallback. Test environments have no image loader at all, so they
   * pass something tiny.
   */
  sheetTimeoutMs?: number;
  /**
   * Load a sheet some other way than `new Image()`: the headless preview tool
   * decodes the PNGs itself. Resolve null for "failed".
   */
  loadImage?: (url: string) => Promise<CanvasImageSource | null>;
  /**
   * Fetch the sheets from this base URL instead of where the bundler put them
   * (e.g. '/sprites/' or a CDN). Only the file name of each sheet is kept.
   */
  atlasBase?: string;
}

/** `https://x/assets/stage-abc.png` + base `/sprites/` -> `/sprites/stage-abc.png`. */
export function rebaseSheetUrl(url: string, base: string | undefined): string {
  if (!base) return url;
  const name = url.split(/[?#]/)[0]!.split('/').pop() ?? url;
  return `${base.endsWith('/') ? base : `${base}/`}${name}`;
}

async function loadManifest(): Promise<AtlasManifest | null> {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const mods = import.meta.glob('./atlas.ts') as Record<string, () => Promise<unknown>>;
  const loaders = Object.values(mods);
  const loader = loaders[0];
  if (!loader) return null;
  try {
    const mod = (await loader()) as { default?: AtlasManifest } | undefined;
    const manifest = mod?.default;
    if (!manifest || typeof manifest !== 'object' || !manifest.sprites || !manifest.sheets) {
      return null;
    }
    return manifest;
  } catch {
    return null;
  }
}

export class SpriteSystem {
  private manifest: AtlasManifest | null = null;
  private readonly images = new Map<string, CanvasImageSource>();
  private readonly loadImage: SpriteSystemOptions['loadImage'];
  private readonly atlasBase: string | undefined;
  /** Bumped whenever a sheet settles, so caches of drawn sprites know to repaint. */
  private gen = 0;
  private readonly sheetState = new Map<string, SheetState>();
  private readonly missing = new Set<string>(REQUIRED_SPRITES);
  private readonly failed = new Set<string>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly sheetTimeoutMs: number;
  private disposed = false;
  private resolveReady!: () => void;
  /** Canvas draw ops issued since the last `consumeOps()`. */
  private ops = 0;
  /** Sprite draw *requests* (atlas or fallback) since the last `consumeRequests()`. */
  private requests = 0;

  readonly ready: Promise<void>;

  constructor(opts: SpriteSystemOptions = {}) {
    this.sheetTimeoutMs = Math.max(1, opts.sheetTimeoutMs ?? DEFAULT_SHEET_TIMEOUT_MS);
    this.loadImage = opts.loadImage;
    this.atlasBase = opts.atlasBase;
    this.ready = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
    void this.boot();
  }

  private async boot(): Promise<void> {
    const manifest = await loadManifest();
    if (this.disposed) {
      this.resolveReady();
      return;
    }
    this.manifest = manifest;
    if (manifest) {
      for (const key of Object.keys(manifest.sprites)) {
        const def = manifest.sprites[key];
        if (def && def.frames.length > 0 && manifest.sheets[def.sheet]) this.missing.delete(key);
      }
      await Promise.all(
        Object.entries(manifest.sheets).map(([name, url]) => this.loadSheet(name, rebaseSheetUrl(url, this.atlasBase))),
      );
    }
    this.resolveReady();
  }

  private loadSheet(name: string, url: string): Promise<void> {
    this.sheetState.set(name, 'pending');
    const custom = this.loadImage;
    if (custom) {
      return custom(url)
        .catch(() => null)
        .then((img) => {
          if (this.disposed) return;
          if (img) {
            this.sheetState.set(name, 'ready');
            this.images.set(name, img);
          } else {
            this.sheetState.set(name, 'failed');
            this.failed.add(name);
          }
          this.gen++;
        });
    }
    return new Promise<void>((resolve) => {
      if (typeof Image !== 'function' || !url) {
        this.sheetState.set(name, 'failed');
        this.failed.add(name);
        resolve();
        return;
      }
      const img = new Image();
      let settled = false;
      const timer = setTimeout(() => finish(false), this.sheetTimeoutMs);
      this.timers.add(timer);
      const finish = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        this.timers.delete(timer);
        if (ok) {
          this.sheetState.set(name, 'ready');
          this.images.set(name, img);
          this.failed.delete(name);
        } else {
          this.sheetState.set(name, 'failed');
          this.failed.add(name);
        }
        this.gen++;
        resolve();
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      try {
        img.src = url;
      } catch {
        finish(false);
      }
    });
  }

  /** True once the atlas manifest resolved. Sheets may still be loading. */
  get loaded(): boolean {
    return this.manifest !== null;
  }

  /** Changes whenever a sheet finishes (or fails) loading. */
  get generation(): number {
    return this.gen;
  }

  /** True when `key` will blit from a decoded sheet rather than a fallback. */
  has(key: string): boolean {
    return this.def(key) !== null;
  }

  /**
   * Sprite keys the atlas does not declare. Empty once ART lands, in every
   * environment — a sheet that fails to download is reported separately by
   * `failedSheets()` so this list stays a pure statement about the manifest.
   */
  missingSprites(): string[] {
    return [...this.missing].sort();
  }

  /** Sheets that could not be decoded. Non-empty means the art is broken. */
  failedSheets(): string[] {
    return [...this.failed].sort();
  }

  private def(key: string): SpriteDef | null {
    const m = this.manifest;
    if (!m) return null;
    const def = m.sprites[key];
    if (!def || def.frames.length === 0) return null;
    if (this.sheetState.get(def.sheet) !== 'ready') return null;
    return def;
  }

  frameCount(key: string): number {
    return this.manifest?.sprites[key]?.frames.length ?? 1;
  }

  /**
   * Natural size of a sprite's first frame, or null when the atlas has not
   * declared it. Lets the scene anchor art it did not author.
   */
  frameSize(key: string): { w: number; h: number } | null {
    const f = this.manifest?.sprites[key]?.frames[0];
    return f ? { w: f.w, h: f.h } : null;
  }

  /** Frame index for an animated sprite at `timeS`, wrapping on the loop. */
  frameIndex(key: string, timeS: number): number {
    const def = this.manifest?.sprites[key];
    const n = def?.frames.length ?? 1;
    if (n <= 1) return 0;
    const fps = def?.fps && def.fps > 0 ? def.fps : DEFAULT_FPS;
    const i = Math.floor(timeS * fps) % n;
    return i < 0 ? i + n : i;
  }

  /** Blit from the atlas. Returns false when the caller should paint a fallback. */
  tryDraw(
    ctx: CanvasRenderingContext2D,
    key: string,
    x: number,
    y: number,
    opts?: SpriteDrawOptions,
  ): boolean {
    this.requests++;
    const def = this.def(key);
    if (!def) {
      if (this.manifest !== null && !this.manifest.sprites[key]) this.missing.add(key);
      return false;
    }
    const img = this.images.get(def.sheet);
    if (!img) return false;

    const n = def.frames.length;
    let idx = opts?.frame ?? this.frameIndex(key, opts?.timeS ?? 0);
    if (!Number.isFinite(idx)) idx = 0;
    idx = Math.min(n - 1, Math.max(0, Math.floor(idx)));
    const f = def.frames[idx]!;

    const alpha = opts?.alpha ?? 1;
    const prevAlpha = ctx.globalAlpha;
    if (alpha !== 1) ctx.globalAlpha = prevAlpha * alpha;

    const dw = opts?.stretch && opts.w && opts.w > 0 ? opts.w : f.w;
    const dh = opts?.stretch && opts.h && opts.h > 0 ? opts.h : f.h;
    ctx.drawImage(
      img,
      f.x,
      f.y,
      f.w,
      f.h,
      Math.round(x + (f.ox ?? 0)),
      Math.round(y + (f.oy ?? 0)),
      dw,
      dh,
    );
    this.ops++;
    if (alpha !== 1) ctx.globalAlpha = prevAlpha;
    return true;
  }

  /** Blit from the atlas, or paint the procedural stand-in. Never throws. */
  draw(
    ctx: CanvasRenderingContext2D,
    key: string,
    x: number,
    y: number,
    opts?: SpriteDrawOptions,
  ): void {
    if (this.tryDraw(ctx, key, x, y, opts)) return;
    const alpha = opts?.alpha ?? 1;
    const prevAlpha = ctx.globalAlpha;
    if (alpha !== 1) ctx.globalAlpha = prevAlpha * alpha;
    drawFallbackSprite(
      ctx,
      key,
      x,
      y,
      opts?.w ?? 16,
      opts?.h ?? 16,
      opts?.frame ?? this.frameIndex(key, opts?.timeS ?? 0),
      opts?.timeS ?? 0,
      opts?.extra ?? 0,
    );
    this.ops += consumeFallbackOps();
    if (alpha !== 1) ctx.globalAlpha = prevAlpha;
  }

  /** Draw-op count since the last call. */
  consumeOps(): number {
    const n = this.ops;
    this.ops = 0;
    return n;
  }

  /** Sprite draw requests since the last call — the `sprites` render stat. */
  consumeRequests(): number {
    const n = this.requests;
    this.requests = 0;
    return n;
  }

  destroy(): void {
    this.disposed = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    for (const img of this.images.values()) {
      if (typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement) {
        img.onload = null;
        img.onerror = null;
      }
    }
    this.images.clear();
  }
}
